import * as React from 'react';
import {
  ActionButton, ChoiceGroup, DefaultButton, Dropdown, type IChoiceGroupOption, type IDropdownOption, IconButton,
  MessageBar, MessageBarType, Pivot, PivotItem, PrimaryButton, Spinner, TextField
} from '@fluentui/react';
import styles from './SharePointOrganizer.module.scss';
import type { IAuditLog } from '../../../engine/AuditLog';
import { ChangeEngine } from '../../../engine/ChangeEngine';
import type { IDriveWriter } from '../../../engine/DriveWriter';
import type { IChangeDecision } from '../../../models/Plan';
import type { IScanResult } from '../../../models/ScanResult';
import { type IResolvedChange, resolveChange, simulatePlan } from '../../../plan/PlanSimulator';
import { values } from '../../../rules/ruleUtils';
import { type IStructureProposal, proposeStructure, STRUCTURE_TEMPLATES } from '../../../structure/StructureWizard';
import type { ITagSet } from '../../../tagging/Tagging';
import { describeGraphError } from '../../../utils/errors';
import { formatNumber } from '../../../utils/format';
import type { ITelemetry } from '../../../utils/telemetry';
import ApplyPanel from './ApplyPanel';
import BeforeAfterView from './BeforeAfterView';
import ChangeCard from './ChangeCard';

export interface IStructureViewProps {
  scan: IScanResult;
  auditLog: IAuditLog;
  createWriter: () => IDriveWriter;
  /** Reads tags already on files, when the host supports tagging (web app). */
  loadTags?: (fileIds: string[]) => Promise<{ [fileId: string]: ITagSet }>;
  userName: string;
  allowChanges: boolean;
  telemetry: ITelemetry;
  onRescan: () => void;
}

const CUSTOM: string = 'custom';
const MIN_CATEGORIES: number = 3;
const MAX_CATEGORIES: number = 10;

function plural(n: number, one: string, many: string): string {
  return `${formatNumber(n)} ${n === 1 ? one : many}`;
}

/**
 * "Set up a structure": pick a template of a few top-level categories, see where every
 * folder and file would go, adjust, approve, apply — undoable like every other change.
 */
export default function StructureView(props: IStructureViewProps): React.ReactElement {
  const { scan, auditLog, createWriter, loadTags, userName, allowChanges, telemetry, onRescan } = props;
  const [basePath, setBasePath] = React.useState<string>('');
  const [templateId, setTemplateId] = React.useState<string>(STRUCTURE_TEMPLATES[0].id);
  const [categories, setCategories] = React.useState<string[]>(STRUCTURE_TEMPLATES[0].categories);
  const [proposal, setProposal] = React.useState<IStructureProposal | undefined>(undefined);
  const [decisions, setDecisions] = React.useState<{ [id: string]: IChangeDecision }>({});
  const [building, setBuilding] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [applying, setApplying] = React.useState<{ engine: ChangeEngine; changes: IResolvedChange[] } | undefined>(undefined);

  React.useEffect(() => { setProposal(undefined); setDecisions({}); }, [scan]);

  const baseOptions: IDropdownOption[] = [{ key: '', text: `Library root (${scan.library.name})` }].concat(
    values(scan.folders)
      .filter(f => f.id !== scan.rootId && f.childFolderIds.length + f.fileIds.length > 0)
      .sort((a, b) => a.path.localeCompare(b.path))
      .map(f => ({ key: f.path, text: f.path }))
  );
  const templateOptions: IChoiceGroupOption[] = STRUCTURE_TEMPLATES
    .map(t => ({ key: t.id, text: `${t.name} — ${t.description}` }))
    .concat([{ key: CUSTOM, text: 'Custom — start from a blank list' }]);

  const pickTemplate = (id: string): void => {
    setTemplateId(id);
    const template = STRUCTURE_TEMPLATES.filter(t => t.id === id)[0];
    setCategories(template ? template.categories.slice() : ['', '', '', '', '']);
    setProposal(undefined);
  };

  const names: string[] = categories.map(c => c.trim()).filter(c => !!c);
  const duplicate: boolean = names.some((n, i) => names.map(x => x.toLowerCase()).indexOf(n.toLowerCase()) !== i);
  const invalid: boolean = names.some(n => /["*:<>?/\\|]/.test(n));
  const canBuild: boolean = names.length >= MIN_CATEGORIES && names.length <= MAX_CATEGORIES && !duplicate && !invalid;

  const build = (): void => {
    setBuilding(true);
    setError(undefined);
    const scope: string[] = values(scan.files)
      .filter(f => !basePath || f.parentPath === basePath || f.parentPath.indexOf(`${basePath}/`) === 0)
      .map(f => f.id);
    (loadTags ? loadTags(scope) : Promise.resolve({}))
      .catch(() => ({})) // Tags are a bonus; the proposal works without them.
      .then(tags => {
        const p: IStructureProposal = proposeStructure(scan, { basePath, categories: names, tags });
        setProposal(p);
        setDecisions({});
        telemetry.event('structure.proposed', {
          libraryId: scan.library.id, categories: names.length, changes: p.plan.changes.length,
          needsInput: p.plan.changes.filter(c => c.needsInput).length, kept: p.keptFolders.length
        });
      })
      .catch(e => setError(describeGraphError(e)))
      .then(() => setBuilding(false), () => setBuilding(false));
  };

  const resolved: IResolvedChange[] = proposal ? proposal.plan.changes.map(c => resolveChange(scan, c, decisions[c.id])) : [];
  const approvable: string[] = resolved.filter(r => r.status === 'pending' && !r.error).map(r => r.change.id);
  const ready: IResolvedChange[] = resolved.filter(r => r.status === 'approved' && !r.error);
  const preview = React.useMemo(
    () => (proposal ? simulatePlan(scan, proposal.plan, decisions, true) : undefined),
    [scan, proposal, decisions]
  );

  return (
    <div className={styles.plan}>
      <h3 className={styles.sectionTitle}>Set up a structure</h3>
      <p className={styles.issueListDescription}>
        Choose a handful of top-level categories. Existing folders move into them as a whole; loose files and the contents
        of catch-all folders (Misc, Old…) are sorted one by one. Folders that already are a category stay where they are.
        Nothing moves until you approve and apply, and it can all be undone from History.
      </p>

      <div className={styles.structureSetup}>
        <Dropdown label="Organize inside" options={baseOptions} selectedKey={basePath}
          onChange={(_, o) => { if (o) { setBasePath(String(o.key)); setProposal(undefined); } }} />
        <ChoiceGroup label="Start from" options={templateOptions} selectedKey={templateId} onChange={(_, o) => o && pickTemplate(String(o.key))} />
        <div>
          <div className={styles.structureLabel}>Categories ({MIN_CATEGORIES}–{MAX_CATEGORIES})</div>
          <ul className={styles.categoryList}>
            {categories.map((c, i) => (
              <li key={i}>
                <TextField ariaLabel={`Category ${i + 1}`} value={c} placeholder="Category name"
                  onChange={(_, v) => { const next = categories.slice(); next[i] = v ?? ''; setCategories(next); setProposal(undefined); }} />
                <IconButton iconProps={{ iconName: 'Cancel' }} ariaLabel="Remove category"
                  onClick={() => { setCategories(categories.filter((_, j) => j !== i)); setProposal(undefined); }} />
              </li>
            ))}
          </ul>
          <ActionButton iconProps={{ iconName: 'Add' }} text="Add a category" disabled={categories.length >= MAX_CATEGORIES}
            onClick={() => setCategories(categories.concat(['']))} />
          {duplicate && <MessageBar messageBarType={MessageBarType.warning}>Two categories have the same name.</MessageBar>}
          {invalid && <MessageBar messageBarType={MessageBarType.warning}>Category names can&apos;t contain &quot; * : &lt; &gt; ? / \ |</MessageBar>}
        </div>
        <div className={styles.changeActions}>
          <PrimaryButton text={building ? 'Working it out…' : 'Show me the proposal'} iconProps={{ iconName: 'Org' }}
            disabled={!canBuild || building} onClick={build} />
        </div>
        {building && <Spinner label="Matching folders and files to categories…" />}
        {error && <MessageBar messageBarType={MessageBarType.error} isMultiline>{error}</MessageBar>}
      </div>

      {proposal && (
        <>
          <MessageBar isMultiline>
            {plural(proposal.plan.changes.filter(c => c.itemKind === 'folder').length, 'folder', 'folders')} and{' '}
            {plural(proposal.plan.changes.filter(c => c.itemKind === 'file').length, 'file', 'files')} to place;{' '}
            {plural(proposal.plan.changes.filter(c => c.needsInput).length, 'needs', 'need')} you to choose.
            {proposal.keptFolders.length > 0 && ` Already categories, staying put: ${proposal.keptFolders.join(', ')}.`}
            {' '}Missing category folders are created when you apply.
          </MessageBar>
          {!allowChanges && (
            <MessageBar isMultiline>
              <strong>Recommend-only mode.</strong> To apply the structure, turn on <strong>Allow changes to this site</strong>.
            </MessageBar>
          )}
          <div className={styles.planToolbar}>
            <span className={styles.planProgress}>{formatNumber(ready.length)} approved</span>
            <DefaultButton text={`Approve all suggestions (${formatNumber(approvable.length)})`} iconProps={{ iconName: 'CheckList' }}
              disabled={approvable.length === 0}
              onClick={() => setDecisions(prev => {
                const next = { ...prev };
                for (const id of approvable) { next[id] = { ...(prev[id] ?? {}), status: 'approved' }; }
                return next;
              })} />
            <PrimaryButton text={`Apply approved (${formatNumber(ready.length)})`} iconProps={{ iconName: 'Play' }}
              disabled={!allowChanges || ready.length === 0}
              onClick={() => setApplying({
                engine: new ChangeEngine({ scan, plan: proposal.plan, writer: createWriter(), audit: auditLog, user: userName }),
                changes: ready
              })} />
          </div>
          <Pivot headersOnly={false}>
            <PivotItem headerText="Changes">
              <ul className={styles.changes}>
                {resolved.map(r => (
                  <ChangeCard key={r.change.id} scan={scan} plan={proposal.plan} resolved={r} decision={decisions[r.change.id]}
                    conflicts={[]} onDecide={d => setDecisions(prev => ({ ...prev, [r.change.id]: d }))} />
                ))}
              </ul>
            </PivotItem>
            <PivotItem headerText="Before / after">
              {preview && (
                <BeforeAfterView libraryName={scan.library.name} preview={preview} includePending onIncludePendingChange={() => undefined} />
              )}
            </PivotItem>
          </Pivot>
        </>
      )}

      {applying && proposal && (
        <ApplyPanel scan={scan} plan={proposal.plan} changes={applying.changes} engine={applying.engine}
          auditLogUrl={auditLog.viewUrl} telemetry={telemetry}
          onDismiss={() => setApplying(undefined)}
          onRescan={() => { setApplying(undefined); onRescan(); }} />
      )}
    </div>
  );
}
